import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Language } from '../types';
import { TRANSLATIONS } from '../i18n/translations';
import { easywayUrl, normalizeSpanishSlug } from '../lib/easyway';

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  toggleLang: () => void;
  easyWayUrl: string;
  easyWayFleetUrl: string;
  easyWayCancunAirportUrl: string;
  easyWayPlayaDelCarmenUrl: string;
  easyWayContactUrl: string;
  easyWayWhatsAppUrl: string;
  t: typeof TRANSLATIONS['es'];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const EASY_WAY_URL_ES = 'https://easywayrentacar.com/es';
export const EASY_WAY_URL_EN = 'https://easywayrentacar.com/';
export const EASY_WAY_URL_PT = 'https://easywayrentacar.com/pt';

export const EASY_WAY_FLEET_URL_ES = 'https://easywayrentacar.com/es/cars';
export const EASY_WAY_FLEET_URL_EN = 'https://easywayrentacar.com/cars';
export const EASY_WAY_FLEET_URL_PT = 'https://easywayrentacar.com/pt/cars';

export const EASY_WAY_AIRPORT_URL_ES = 'https://easywayrentacar.com/es/cancun-airport-car-rental';
export const EASY_WAY_AIRPORT_URL_EN = 'https://easywayrentacar.com/cancun-airport-car-rental';
export const EASY_WAY_AIRPORT_URL_PT = 'https://easywayrentacar.com/cancun-airport-car-rental';

export const EASY_WAY_PDC_URL_ES = 'https://easywayrentacar.com/es/car-rental-riviera-maya';
export const EASY_WAY_PDC_URL_EN = 'https://easywayrentacar.com/car-rental-riviera-maya';
export const EASY_WAY_PDC_URL_PT = 'https://easywayrentacar.com/car-rental-riviera-maya';

export const EASY_WAY_CONTACT_URL_ES = 'https://easywayrentacar.com/es/contact';
export const EASY_WAY_CONTACT_URL_EN = 'https://easywayrentacar.com/contact';
export const EASY_WAY_CONTACT_URL_PT = 'https://easywayrentacar.com/pt/contact';

export const EASY_WAY_WHATSAPP_ES = 'https://wa.me/5219981255280?text=Hola%20Easy%20Way%2C%20quisiera%20cotizar%20o%20reservar%20un%20auto%20para%20Cancun%20y%20Riviera%20Maya';
export const EASY_WAY_WHATSAPP_EN = 'https://wa.me/5219981255280?text=Hello%20Easy%20Way%2C%20I%20would%20like%20to%20quote%20or%20reserve%20a%20car%20for%20Cancun%20%26%20Riviera%20Maya';
export const EASY_WAY_WHATSAPP_PT = 'https://wa.me/5219981255280?text=Olá%20Easy%20Way%2C%20gostaria%20de%20fazer%20uma%20cotação%20ou%20reservar%20um%20carro%20para%20Cancun%20e%20Riviera%20Maya';


function getLanguageFromPathname(pathname: string): Language {
  const match = pathname.match(/^\/(es|en|pt)(\/|$)/i);
  if (match) {
    return match[1].toLowerCase() as Language;
  }
  return 'es';
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  let locationPath = '/';
  try {
    const loc = useLocation();
    locationPath = loc.pathname;
  } catch {
    // Fallback if rendered outside Router context
  }

  const urlLang = useMemo(() => getLanguageFromPathname(locationPath), [locationPath]);
  const [lang, setLang] = useState<Language>(urlLang);

  useEffect(() => {
    setLang(urlLang);
  }, [urlLang]);

  const toggleLang = () => {
    if (lang === 'es') setLang('en');
    else if (lang === 'en') setLang('pt');
    else setLang('es');
  };

  // Synchronize document attributes and SEO metadata whenever language changes
  useEffect(() => {
    const texts = TRANSLATIONS[lang];

    // 1. HTML lang attribute (crucial for SEO screen readers & Googlebot)
    document.documentElement.lang = lang;

    // 2. Document title
    document.title = texts.seo.title;

    // 3. Meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', texts.seo.description);

    // 4. Meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', texts.seo.keywords);
    }

    // 5. Open Graph tags
    const updateOrCreateMeta = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    updateOrCreateMeta('og:title', texts.seo.ogTitle);
    updateOrCreateMeta('og:description', texts.seo.ogDescription);
    updateOrCreateMeta('og:locale', texts.seo.locale);
    updateOrCreateMeta('og:locale:alternate', lang === 'es' ? 'en_US' : lang === 'en' ? 'es_MX' : 'pt_BR');

    // 6. Twitter card tags
    const updateTwitterMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    updateTwitterMeta('twitter:title', texts.seo.ogTitle);
    updateTwitterMeta('twitter:description', texts.seo.ogDescription);

  }, [lang]);

  const currentSlug = useMemo(() => {
    return normalizeSpanishSlug(locationPath);
  }, [locationPath]);

  const rawEasyWayUrl = lang === 'pt' ? EASY_WAY_URL_PT : lang === 'en' ? EASY_WAY_URL_EN : EASY_WAY_URL_ES;
  const rawEasyWayFleetUrl = lang === 'pt' ? EASY_WAY_FLEET_URL_PT : lang === 'en' ? EASY_WAY_FLEET_URL_EN : EASY_WAY_FLEET_URL_ES;
  const rawEasyWayCancunAirportUrl = lang === 'pt' ? EASY_WAY_AIRPORT_URL_PT : lang === 'en' ? EASY_WAY_AIRPORT_URL_EN : EASY_WAY_AIRPORT_URL_ES;
  const rawEasyWayPlayaDelCarmenUrl = lang === 'pt' ? EASY_WAY_PDC_URL_PT : lang === 'en' ? EASY_WAY_PDC_URL_EN : EASY_WAY_PDC_URL_ES;
  const rawEasyWayContactUrl = lang === 'pt' ? EASY_WAY_CONTACT_URL_PT : lang === 'en' ? EASY_WAY_CONTACT_URL_EN : EASY_WAY_CONTACT_URL_ES;
  const easyWayWhatsAppUrl = lang === 'pt' ? EASY_WAY_WHATSAPP_PT : lang === 'en' ? EASY_WAY_WHATSAPP_EN : EASY_WAY_WHATSAPP_ES;

  const easyWayUrl = easywayUrl(rawEasyWayUrl, currentSlug);
  const easyWayFleetUrl = easywayUrl(rawEasyWayFleetUrl, currentSlug);
  const easyWayCancunAirportUrl = easywayUrl(rawEasyWayCancunAirportUrl, currentSlug);
  const easyWayPlayaDelCarmenUrl = easywayUrl(rawEasyWayPlayaDelCarmenUrl, currentSlug);
  const easyWayContactUrl = easywayUrl(rawEasyWayContactUrl, currentSlug);
  const t = TRANSLATIONS[lang];

  return (
    <LanguageContext.Provider
      value={{
        lang,
        setLang,
        toggleLang,
        easyWayUrl,
        easyWayFleetUrl,
        easyWayCancunAirportUrl,
        easyWayPlayaDelCarmenUrl,
        easyWayContactUrl,
        easyWayWhatsAppUrl,
        t,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
